import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ScrollToTop from "./components/ScrollToTop";
import ScrollToSectionButton from "./components/ScrollToSectionButton";
import axiosInstance from "./utils/axios";
import { getOrCreateAnonymousId } from "./utils/anonymousId";
// Auth
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
// Restaurant
import RoleBasedRoute from "./components/RoleBasedRoute";
import OwnerLayout from "./pages/owner/OwnerLayout";
import Dashboard from "./pages/owner/Dashboard";
import Menu from "./pages/owner/Menu";
import Orders from "./pages/owner/Orders";
import QR from "./pages/owner/QR";
import StaffManagement from "./pages/owner/StaffManagement";
import CodSettlements from "./pages/owner/CodSettlements";
import OwnerCommission from "./pages/owner/OwnerCommission";
import Billing from "./pages/owner/Billing";
// import PaymentsList from "./pages/owner/PaymentsList";
import Profile from "./pages/owner/Profile";
import Category from "./pages/owner/Category";
import RestaurantLayout from "./pages/public/Restaurantlayout";
import HomeBanner from "./pages/owner/HomeBanner";
import Offers from "./pages/owner/Offers";
import PublicMenuPage from "./pages/public/tabs/PublicMenuPage";

// Per Login
import Home from "./pages/Home";
import About from "./pages/About";
import Features from "./pages/Features";
import Pricing from "./pages/Pricing";
import Contact from "./pages/Contact";
import FAQ from "./pages/FAQ";
import Help from "./pages/Help";
import Restaurant from "./pages/Restaurant";
import DeliveryLogin from "./pages/delivery/DeliveryLogin";
import DeliveryForgotPassword from "./pages/delivery/DeliveryForgotPassword";
import DeliveryDashboard from "./pages/delivery/DeliveryDashboard";
import NotFound from "./pages/NotFound";

// Super Admin Dashboar
import AdminProtectedRoute from "./components/AdminProtectedRoute";
import SuperAdminLayout from "./SuperAdmin/SuperAdminLayout";
import SuperAdminLogin from "./SuperAdmin/SuperAdminLogin";
import AdminDashboard from "./SuperAdmin/AdminDashboard";
import AdminRestaurants from "./SuperAdmin/AdminRestaurants";
import RestaurantDetails from "./SuperAdmin/RestaurantDetails";
import AdminInvoices from "./SuperAdmin/AdminInvoices";
import AdminPayments from "./SuperAdmin/AdminPayments";
import AdminContact from "./SuperAdmin/AdminContact";
import AdminDeliveryBoys from "./SuperAdmin/AdminDeliveryBoys";
import AdminCommissionSettlements from "./SuperAdmin/AdminCommissionSettlements";

import {
  initializeAuth,
  selectIsAuthenticated,
  selectUser,
} from "./redux/slices/authSlice";

import { setupPushNotifications } from "./utils/pushNotifications";

// Component to redirect authenticated users away from public routes
const RedirectIfAuthenticated = ({ children }) => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  // ya spinner dikha sakte ho
  if (isAuthenticated) {
    // Sabhi authenticated users ko owner dashboard par bhejo
    return <Navigate to="/owner/dashboard" replace />;
  }

  return children;
};

function App() {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);

  useEffect(() => {
    dispatch(initializeAuth());
  }, [dispatch]);

  useEffect(() => {
    // 🔥 Warm-up: Cookie set karne ke liye
    const warmUp = async () => {
      try {
        const anonId = getOrCreateAnonymousId();
        await axiosInstance.post("/api/auth/anonymous-session", {
          anonymousId: anonId,
        });
      } catch (e) {
        // Ignore errors
      }
    };
    warmUp();
  }, []);

  useEffect(() => {
    if (user && (user.role === "OWNER" || user.role === "ADMIN")) {
      const restaurantId =
        user.effectiveRestaurantId || user.restaurantId || user._id;
      if (restaurantId) {
        // Service worker register & push notification token save karein
        setupPushNotifications(restaurantId);
      }
    }
  }, [user]);

  return (
    <BrowserRouter>
      <ScrollToTop smooth={true} delay={250} />
      {/* <ScrollToSectionButton showAfter={200} tooltip="Go to top" /> */}
      <Routes>
        {/* Public Routes */}
        <Route
          path="/"
          element={
            <RedirectIfAuthenticated>
              <Home />
            </RedirectIfAuthenticated>
          }
        />
        <Route path="/about" element={<About />} />
        <Route path="/features" element={<Features />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/help" element={<Help />} />
        <Route path="/restaurants" element={<Restaurant />} />
        <Route
          path="/login"
          element={
            <RedirectIfAuthenticated>
              <Login />
            </RedirectIfAuthenticated>
          }
        />
        <Route
          path="/register"
          element={
            <RedirectIfAuthenticated>
              <Register />
            </RedirectIfAuthenticated>
          }
        />

        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />

        {/* // Delivery portal public & dashboard routes: */}
        <Route path="/delivery/login" element={<DeliveryLogin />} />
        <Route path="/delivery/forgot-password" element={<DeliveryForgotPassword />} />
        <Route path="/delivery/dashboard" element={<DeliveryDashboard />} />
        
        
        {/* Truly Public Routes */}
        <Route path="/menu/:restaurantId" element={<PublicMenuPage />} />
        <Route
          path="/restaurant/:restaurantId/*"
          element={<RestaurantLayout />}
        />
        {/* Restaurant Routes */}
        {/* Protected Routes - Sabhi authenticated users ke liye (OWNER, MANAGER, STAFF, CASHIER, KITCHEN) */}
        <Route element={<RoleBasedRoute allowedRoles={[]} />}>
          {" "}
          {/* Empty array = all authenticated users */}
          <Route path="/owner" element={<OwnerLayout />}>
            <Route index element={<Dashboard />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="category" element={<Category />} />
            <Route path="menu" element={<Menu />} />
            <Route path="orders" element={<Orders />} />
            <Route path="qr" element={<QR />} />
            <Route path="profile" element={<Profile />} />
            <Route path="offers" element={<Offers />} />
            <Route path="banner" element={<HomeBanner />} />
            <Route path="staff" element={<StaffManagement />} />
             <Route path="cod-settlements" element={<CodSettlements />} />
             <Route path="commission" element={<OwnerCommission />} />
            <Route path="Billing" element={<Billing />} />
            {/* <Route path="payments" element={<PaymentsList />} />  */}
          </Route>
        </Route>

        {/* Super Admin Dashboard */}
        <Route path="/admin/login" element={<SuperAdminLogin />} />
        <Route
          path="/admin"
          element={
            <AdminProtectedRoute>
              <SuperAdminLayout />
            </AdminProtectedRoute>
          }
        >
          <Route path="dashboard" element={<AdminDashboard />} />

          <Route path="restaurants" element={<AdminRestaurants />} />

          <Route
            path="/admin/restaurants/:id"
            element={
              <AdminProtectedRoute>
                <RestaurantDetails />
              </AdminProtectedRoute>
            }
          />

          <Route path="invoices" element={<AdminInvoices />} />

          <Route path="payments" element={<AdminPayments />} />
          <Route path="contact" element={<AdminContact />} />
          <Route path="delivery-boys" element={<AdminDeliveryBoys />} />
          <Route path="commission-settlements" element={<AdminCommissionSettlements />} />
        </Route>

        {/* Catch All */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      <ToastContainer position="top-right" autoClose={3000} />
    </BrowserRouter>
  );
}

export default App;

// import React, { useEffect } from "react";
// import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
// import { useDispatch, useSelector } from "react-redux";
// import { ToastContainer } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import Login from "./pages/auth/Login";
// import Register from "./pages/auth/Register";
// import RoleBasedRoute from "./components/RoleBasedRoute";
// import OwnerLayout from "./pages/owner/OwnerLayout";
// import Dashboard from "./pages/owner/Dashboard";
// import Menu from "./pages/owner/Menu";
// import Orders from "./pages/owner/Orders";
// import QR from "./pages/owner/QR";
// import StaffManagement from "./pages/owner/StaffManagement";
// import Profile from "./pages/owner/Profile";
// import Category from "./pages/owner/Category";
// import RestaurantLayout from "./pages/public/Restaurantlayout";
// import HomeBanner from "./pages/owner/HomeBanner";
// import Offers from "./pages/owner/Offers";
// import PublicMenuPage from "./pages/public/tabs/PublicMenuPage";
// import NotFound from "./pages/Error";
// import Home from "./pages/Home";
// import { initializeAuth, selectIsAuthenticated, selectUser } from "./redux/slices/authSlice";

// // Component to redirect authenticated users away from public routes
// const RedirectIfAuthenticated = ({ children }) => {
//   const isAuthenticated = useSelector(selectIsAuthenticated);
//   const user = useSelector(selectUser);

//   if (isAuthenticated) {
//     // If user is owner/admin, redirect to owner dashboard
//     if (user?.role === "OWNER" || user?.role === "ADMIN") {
//       return <Navigate to="/owner/dashboard" replace />;
//     }
//     // Add other role redirects here if needed
//     return <Navigate to="/" replace />;
//   }

//   return children;
// };

// function App() {
//   const dispatch = useDispatch();

//   // Initialize auth on app start
//   useEffect(() => {
//     dispatch(initializeAuth());
//   }, [dispatch]);

//   return (
//     <BrowserRouter>
//       <Routes>
//         {/* Public Routes - with redirect for authenticated users */}
//         <Route
//           path="/"
//           element={
//             <RedirectIfAuthenticated>
//               <Home />
//             </RedirectIfAuthenticated>
//           }
//         />
//         <Route
//           path="/login"
//           element={
//             <RedirectIfAuthenticated>
//               <Login />
//             </RedirectIfAuthenticated>
//           }
//         />
//         <Route
//           path="/register"
//           element={
//             <RedirectIfAuthenticated>
//               <Register />
//             </RedirectIfAuthenticated>
//           }
//         />

//         {/* Truly Public Routes - no redirect needed */}
//         <Route path="/menu/:restaurantId" element={<PublicMenuPage />} />
//         <Route path="/restaurant/:restaurantId/*" element={<RestaurantLayout />} />

//         {/* Protected Owner Routes */}
//         <Route element={<RoleBasedRoute allowedRoles={["OWNER", "ADMIN"]} />}>
//           <Route path="/owner" element={<OwnerLayout />}>
//             <Route index element={<Dashboard />} />
//             <Route path="dashboard" element={<Dashboard />} />
//             <Route path="category" element={<Category />} />
//             <Route path="menu" element={<Menu />} />
//             <Route path="orders" element={<Orders />} />
//             <Route path="qr" element={<QR />} />
//             <Route path="profile" element={<Profile />} />
//             <Route path="offers" element={<Offers />} />
//             <Route path="banner" element={<HomeBanner />} />
//             <Route path="staff" element={<StaffManagement />} />
//           </Route>
//         </Route>

//         {/* Catch All */}
//         <Route path="*" element={<NotFound />} />
//       </Routes>
//       <ToastContainer position="top-right" autoClose={3000} />
//     </BrowserRouter>
//   );
// }

// export default App;
